import { Box, Button, Grid2, Paper, SvgIconTypeMap, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { approveBookingAPIStatus, cancelBookingAPIStatus, fetchBookingComments, fetchUnit, rejectBookingAPIStatus, singleBooking } from '../../Redux/selectors';
import { APICallStatus, Booking, BookingStatus, BookingType, DataTypes, Unit, UserType } from '../../Types/UnitFeatureTypes';
import UserCard from '../CommonComponents/UserCard';
import UnitCard from '../CommonComponents/UnitCard';
import CommentSection from './CommentSection';
import { useAuth } from '../../context/authContext';
import { Actions } from '../../Redux/Reducer/rootReducer';
import { Check, Close, Event } from '@mui/icons-material';
import { useApp } from '../../context/appContext';
import { OverridableComponent } from '@mui/material/OverridableComponent';

const BookingView: React.FC = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user} = useAuth();
    const IS_MANAGER = user?.type === UserType.MANAGER;
    const { setSnackbar } = useApp();

    const unitList = useSelector(fetchUnit);
    const currentRequestData = useSelector(singleBooking) as Booking;
    const approveApiStatus = useSelector(approveBookingAPIStatus);
    const rejectApiStatus = useSelector(rejectBookingAPIStatus);
    const cancelApiStatus = useSelector(cancelBookingAPIStatus);
    const bookingComments = useSelector(fetchBookingComments);
    const [bookingData, setBookingData] = React.useState<Booking>({
        id: 0,
        created_on: '',
        amenity: {
            id: 0,
            title: '',
            description: '',
            slot_duration: '',
        },
        requesting_user: {
            id: 0,
            name: '',
            email: '',
            phone: ''
        },
        requester_unit: {
            id: 0,
            unit_number: ''
        },
        slots: [],
        booking_start: new Date(),
        booking_end: new Date(),
        status: BookingStatus.HOLD,
        type: BookingType.SINGLE_SLOT,
    });
    const bookingUnit: Unit | undefined =  unitList.find((unitData) => unitData.id === bookingData.requester_unit.id) || undefined;
    

    useEffect(() => {
        if (location.state && location.state.id) {
            dispatch(Actions.fetchBooking.bookingActions.request({ id: location.state.id }));
            dispatch(Actions.fetchBooking.bookingCommentActions.request({ id: location.state.id }));
        }
    }, [location]);

    useEffect(() => {
        if (approveApiStatus === APICallStatus.SUCCESS || rejectApiStatus === APICallStatus.SUCCESS || cancelApiStatus === APICallStatus.SUCCESS) {
            setSnackbar(true, 'Booking status updated successfully', 'success');
            dispatch(Actions.fetchBooking.bookingActions.request({ id: location.state.id }));
            dispatch(Actions.booking.resetStatusRequestAction());
        } else if (approveApiStatus === APICallStatus.FAILURE || rejectApiStatus === APICallStatus.FAILURE || cancelApiStatus === APICallStatus.FAILURE) {
            setSnackbar(true, 'Failed to update booking status', 'error');
            dispatch(Actions.booking.resetStatusRequestAction());
        }
    }, [approveApiStatus, rejectApiStatus, cancelApiStatus]);

    useEffect(() => {
        if (currentRequestData && !Array.isArray(currentRequestData)) {
            setBookingData({
                id: currentRequestData.id,
                created_on: currentRequestData.created_on,
                amenity: currentRequestData.amenity,
                requesting_user: currentRequestData.requesting_user,
                requester_unit: currentRequestData.requester_unit,
                slots: currentRequestData.slots,
                booking_start: currentRequestData.booking_start,
                booking_end: currentRequestData.booking_end,
                status: currentRequestData.status,
                type: currentRequestData.type,
            })
        }
    }, [currentRequestData]);

    const handleApproveBooking = () => {
        if (bookingData.id !== undefined) {
            dispatch(Actions.booking.approveBookingRequestAction.request({ booking_id: bookingData.id }));
        }
    }

    const handleRejectBooking = () => {
        if (bookingData.id !== undefined) {
            dispatch(Actions.booking.rejectBookingRequestAction.request({ booking_id: bookingData.id }));
        }
    }

    const handleCancelBooking = () => {
        if (bookingData.id !== undefined) {
            dispatch(Actions.booking.cancelBookingRequestAction.request({ booking_id: bookingData.id }));
        }
    }

    const handleRescheduleBooking = () => {
        navigate(`/createBookingCalender`, { state: { reschedule: true, booking_id: bookingData.id,  amenity: bookingData.amenity.id, bookingDate: new Date(bookingData.booking_start), bookingTime: bookingData.slots, unit: bookingData.requester_unit, occupant: bookingData.requesting_user } });
    }


    interface ActionButtonProps {
        onClickFunc: () => void;
        hoverColor: string;
        icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
        buttonText: string;
    }

    const getActionButton = ({ onClickFunc, hoverColor, icon, buttonText }: ActionButtonProps): JSX.Element => {
        return <Grid2 sx={{flexGrow: 1 }} size={{xs: 6, sm: 6, md: 3}}>
            <Button onClick={onClickFunc} sx={{width: '100%', '&:hover': {backgroundColor: hoverColor}}} variant="contained">
                {React.createElement(icon, { sx: { mr: 2 } })} {buttonText}
            </Button>
        </Grid2>
    };
        

    const getActionComponents = () => {
        const cancelButton = getActionButton({
            onClickFunc: handleCancelBooking,
            hoverColor: 'error.main',
            icon: Close,
            buttonText: 'Cancel'
        });

        const rescheduleButton = getActionButton({
            onClickFunc: handleRescheduleBooking,
            hoverColor: 'primary.main',
            icon: Event,
            buttonText: 'Reschedule'
        });

        const approveButton = getActionButton({
            onClickFunc: handleApproveBooking,
            hoverColor: 'success.main',
            icon: Check,
            buttonText: 'Approve'
        });

        const rejectButton = getActionButton({
            onClickFunc: handleRejectBooking,
            hoverColor: 'secondary.main',
            icon: Close,
            buttonText: 'Reject'
        });

        return new Date(bookingData.booking_start) > new Date() && bookingData.status !== BookingStatus.CANCELLED && bookingData.status !== BookingStatus.REJECTED ? <Grid2 container sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 2 }} spacing={1}>
            {rescheduleButton}
            {IS_MANAGER && bookingData.amenity.require_approval && <>
                {bookingData.status !== BookingStatus.APPROVED && approveButton}
                {bookingData.status !== BookingStatus.APPROVED && rejectButton}
            </>}
            {cancelButton}
        </Grid2> : null;
    };

    return (
        <>
        <Paper elevation={3} sx={{mb: 2, padding: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between'}}>
            <Box color='primary.main' sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
                <Event sx={{mr:1, height: 40, width: 40}} />
                <Typography variant="h5">Booking</Typography>
            </Box>
        </Paper>  
        <Paper elevation={5} sx={{padding: 4, m: 1, borderRadius: 3}}>
            <Typography variant="h4" gutterBottom>{bookingData.amenity.title} Booking</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <UserCard user={{
                    id: bookingData.requesting_user?.id,
                    avatar: bookingData.requesting_user?.avatar,
                    first_name: bookingData.requesting_user?.name,
                    last_name: '',
                    email: bookingData.requesting_user.email,
                    phone: bookingData.requesting_user.phone
                }} isOwner={bookingUnit?.owner?.id?.toString() === bookingData.requesting_user.id.toString()} />
                <UnitCard unit={bookingUnit} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 5 }}>
                <Typography variant="h6" color="textSecondary">Booking Date: {new Date(bookingData?.booking_start).toDateString()}</Typography>
                <Typography variant="h6" color="textSecondary">Created On: {new Date(bookingData?.created_on).toDateString()}</Typography>
            </Box>
            <Grid2 container spacing={1} sx={{mb:1}}>
                <Grid2>
                    <Typography variant="h6" color="textSecondary">Booked Slots</Typography>
                </Grid2>
                {bookingData?.slots?.map((slot, index) =>
                (
                    <Grid2 key={index}>
                    <Button variant="outlined">
                        {new Date(slot).toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit', hour12: false })}
                    </Button>
                    </Grid2>
                )
                )}
            </Grid2>
            <Typography variant="h6" color="textSecondary">Status: {bookingData?.status}</Typography>
            {getActionComponents()}
        </Paper>
        <Paper elevation={5} sx={{padding: 4, m: 1, borderRadius: 3}}>
            <Typography variant="h5" gutterBottom>Comments</Typography>
            <CommentSection previousComments={bookingComments}/>
        </Paper>
        </>
    );
}

export default BookingView;